import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileText, Users, Wallet, CreditCard, Settings, LogOut, BarChart3, Repeat, History, Activity, TrendingUp, Receipt, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

const Sidebar = ({ open, onClose }) => {
  const { signOut, user, isViewer, isAnalyst } = useAuth();
  const location = useLocation();

  const ANALYST_LINKS = ['/dashboard', '/client-intelligence', '/retainers', '/clients'];
  const allLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/billing-health', icon: Activity, label: 'Billing Health' },
    { to: '/client-intelligence', icon: Users, label: 'Client Intelligence' },
    { to: '/invoices', icon: FileText, label: 'Invoices' },
    { to: '/retainers', icon: Repeat, label: 'Retainers', adminOnly: true },
    { to: '/clients', icon: Users, label: 'Clients' },
    { to: '/banks', icon: Wallet, label: 'Banks' },
    { to: '/payments', icon: CreditCard, label: 'Payments' },
    { to: '/collections', icon: TrendingUp, label: 'Collections' },
    { to: '/expenses', icon: Receipt, label: 'Expenses' },
    { to: '/reports', icon: BarChart3, label: 'Reports' },
    { to: '/audit', icon: History, label: 'Audit Log', adminOnly: true },
    { to: '/settings', icon: Settings, label: 'Settings', adminOnly: true },
  ];
  const links = allLinks.filter(l => {
    if (isAnalyst) return ANALYST_LINKS.includes(l.to);
    if (isViewer && l.adminOnly) return false;
    return true;
  });

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          data-testid="sidebar-backdrop"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
        />
      )}
      <div
        data-testid="sidebar"
        className={`fixed md:static top-0 left-0 z-40 w-64 bg-surface border-r border-border h-screen flex flex-col transform transition-transform duration-200 ease-out md:transform-none ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
      >
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="logo-badge">
            <img src="/assets/wolf-logo.png" alt="Wolf Media" />
          </div>
          <button
            type="button"
            data-testid="sidebar-close"
            onClick={onClose}
            className="md:hidden text-text-secondary hover:text-maroon"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <nav className="flex-1 py-6 overflow-y-auto">
          {links.map((link) => {
            const Icon = link.icon;
            const isActive = location.pathname === link.to || location.pathname.startsWith(link.to + '/');
            return (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase()}`}
                onClick={onClose}
                className={`flex items-center px-6 py-2.5 font-body text-sm transition-colors border-l-2 ${
                  isActive
                    ? 'border-maroon text-maroon bg-cream font-medium'
                    : 'border-transparent text-text-secondary hover:text-maroon hover:bg-cream'
                }`}
              >
                <Icon className="w-4 h-4 mr-3" strokeWidth={1.5} />
                {link.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-border p-6">
          <div className="mb-3 hidden md:block">
            <p className="font-body text-[10px] uppercase tracking-[0.2em] text-text-secondary mb-2">Quick Search</p>
            <p className="font-body text-xs text-text-primary">Press <kbd className="px-1.5 py-0.5 border border-border mono-num text-[10px]">⌘K</kbd></p>
          </div>
          <div className="mb-4 pt-3 border-t border-border">
            <p className="font-body text-[10px] uppercase tracking-[0.2em] text-text-secondary mb-1">Logged in as</p>
            <p className="font-body text-xs text-text-primary truncate">{user?.email}</p>
          </div>
          <button
            data-testid="logout-button"
            onClick={signOut}
            className="flex items-center text-text-secondary hover:text-maroon transition-colors font-body text-xs uppercase tracking-widest"
          >
            <LogOut className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
            Sign Out
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
